const COOKIE_NAME = "ot_session";
const FLOW_SOURCE_COOKIE_NAME = "ot_flow_source";
const FLOW_SOURCE_ATTRIBUTE_KEY = "_oneTimeFlowSource";

export const ONE_TIME_FLOW_SOURCES = {
	DEDICATED_COLLECTION: "dedicated-collection",
};

const getCookieValue = (cookieName) => {
	if (typeof document === "undefined") return null;

	return document.cookie
		.split("; ")
		.find((cookie) => cookie.startsWith(`${cookieName}=`))
		?.split("=")[1] || null;
};

/**
 * Check whether the one-time session cookie exists.
 * Safe for SSR — returns false when `document` is unavailable.
 */
export const getOneTimeSession = () => {
	return getCookieValue(COOKIE_NAME) === "1";
};

export const getOneTimeFlowSource = () => {
	const source = getCookieValue(FLOW_SOURCE_COOKIE_NAME);
	return source ? decodeURIComponent(source) : null;
};

/**
 * Set the one-time session cookie (24 h, lax, site-wide).
 */
export const setOneTimeSession = () => {
	if (typeof document === "undefined") return;
	document.cookie = `${COOKIE_NAME}=1; max-age=86400; path=/; SameSite=Lax`;
};

export const setOneTimeFlowSource = (flowSource) => {
	if (typeof document === "undefined") return;

	if (!flowSource) {
		clearOneTimeFlowSource();
		return;
	}

	document.cookie = `${FLOW_SOURCE_COOKIE_NAME}=${encodeURIComponent(flowSource)}; max-age=86400; path=/; SameSite=Lax`;
};

export const setDedicatedOneTimeSession = () => {
	setOneTimeSession();
	setOneTimeFlowSource(ONE_TIME_FLOW_SOURCES.DEDICATED_COLLECTION);
};

export const isDedicatedOneTimeFlow = () =>
	getOneTimeSession() && getOneTimeFlowSource() === ONE_TIME_FLOW_SOURCES.DEDICATED_COLLECTION;

export const getOneTimeFlowAttributes = (flowSource) =>
	flowSource ? [{ key: FLOW_SOURCE_ATTRIBUTE_KEY, value: flowSource }] : [];

export const hasDedicatedOneTimeFlowItems = (items = []) =>
	items.some((item) =>
		[item, ...(item?.subproducts || [])].some((line) =>
			line?.attributes?.some(
				(attribute) =>
					attribute.key === FLOW_SOURCE_ATTRIBUTE_KEY &&
					attribute.value === ONE_TIME_FLOW_SOURCES.DEDICATED_COLLECTION
			)
		)
	);

export const getCheckoutFlowAttributes = ({ isOneTimePurchase, isDedicatedOneTimeFlow }) => {
	const attributes = [];

	if (isOneTimePurchase) {
		attributes.push({ key: "_skipSubscriptionValidator", value: "true" });
	}

	if (isDedicatedOneTimeFlow) {
		attributes.push(...getOneTimeFlowAttributes(ONE_TIME_FLOW_SOURCES.DEDICATED_COLLECTION));
	}

	return attributes;
};

/**
 * Delete the one-time session cookie.
 */
export const clearOneTimeSession = () => {
	if (typeof document === "undefined") return;
	document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
	clearOneTimeFlowSource();
};

export const clearOneTimeFlowSource = () => {
	if (typeof document === "undefined") return;
	document.cookie = `${FLOW_SOURCE_COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
};
